import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { Form } from "../../components/ui/form";
import { createTheme } from "../../styles/theme";
import { cn } from "../../lib/utils";
import { useForm } from "react-hook-form";
import { ToastContainer, toast } from "react-toastify";
import { useFocusManagement, useFocusTrap, useAriaLive } from "../../hooks/useAccessibility";
import { Spinner } from "../ui/spinner";
import {
  EMAIL_REGEX,
  EMAIL_ERROR_MESSAGE,
  FORMSUBMIT_EMAIL,
  FORMSUBMIT_SUBJECT_CONTACT,
  TOAST_MESSAGES,
  DIALOG_STYLES,
  CONTACT_DIALOG_CONTENT,
} from "../../constants/preLaunch";
import { disabledStyles, focusStyles, hoverStyles } from "../../utils/styles";

interface ContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isLightMode: boolean;
}

interface ContactFormData {
  email: string;
  message: string;
}

function ContactDialog({ isOpen, onClose, isLightMode }: ContactDialogProps) {
  const theme = createTheme(isLightMode);
  const announce = useAriaLive();

  // Accessibility hooks
  useFocusManagement(isOpen);
  const dialogRef = useFocusTrap(isOpen);

  const form = useForm<ContactFormData>({
    defaultValues: {
      email: "",
      message: "",
    },
  });

  const handleClose = () => {
    form.reset();
    announce("Contact dialog closed");
    onClose();
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORMSUBMIT_EMAIL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ...data,
          _subject: FORMSUBMIT_SUBJECT_CONTACT,
          _captcha: "false",
        }),
      });

      if (response.ok) {
        toast.success(TOAST_MESSAGES.contact.success);
        announce("Message sent successfully");
        form.reset();
        onClose();
      } else {
        toast.error(TOAST_MESSAGES.contact.error);
        announce("Error sending message. Please try again.");
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.contact.error);
      announce("Error sending message. Please try again.");
    }
  };

  const inputClasses = cn(theme.components.input);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          ref={dialogRef}
          className={cn(
            "sm:max-w-[600px]",
            "border-2 border-brand-accent-mint",
            "rounded-lg p-6",
            "shadow-lg drop-shadow-md",
            "overflow-hidden",
            "fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2",
            theme.colors.bg.secondary
          )}
          style={{
            minWidth: "300px",
            width: "50%",
            maxWidth: "calc(100% - 2rem)",
            zIndex: 50,
            boxShadow: DIALOG_STYLES.mintGlow,
          }}
          aria-describedby="contact-dialog-description"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-dialog-title"
        >
          <div className="flex justify-center mb-4">
            <img src="/mail-envelop.png" alt="Mail envelope" className="h-16" aria-hidden="true" />
          </div>
          <DialogTitle
            id="contact-dialog-title"
            className={`font-bold font-sans text-3xl ${theme.colors.text.title} text-center`}
          >
            {CONTACT_DIALOG_CONTENT.title}
          </DialogTitle>
          <DialogDescription
            id="contact-dialog-description"
            className={`text-center mb-4 ${theme.colors.text.primary}`}
          >
            {CONTACT_DIALOG_CONTENT.description}
          </DialogDescription>
          <Form {...form}>
            <form
              onSubmit={e => {
                e.preventDefault();
                form.handleSubmit(onSubmit)();
              }}
              className="space-y-4"
              noValidate
            >
              <div className="mb-4">
                <label className={`block text-base font-medium mb-2 ${theme.colors.text.primary}`}>
                  {CONTACT_DIALOG_CONTENT.emailLabel}{" "}
                  <span className="text-brand-accent-coral">*</span>
                </label>
                <input
                  type="email"
                  {...form.register("email", {
                    required: EMAIL_ERROR_MESSAGE,
                    pattern: {
                      value: EMAIL_REGEX,
                      message: EMAIL_ERROR_MESSAGE,
                    },
                  })}
                  disabled={form.formState.isSubmitting}
                  className={cn(
                    "w-full px-4 py-2 text-base rounded-md font-sans",
                    "focus:outline-none focus:ring-2 transition-all duration-200",
                    inputClasses,
                    disabledStyles.default,
                    form.formState.errors.email
                      ? "border-2 border-state-error focus:ring-state-error focus:border-state-error"
                      : "border-2 focus:ring-brand-accent-mint"
                  )}
                  aria-label="Email address"
                  aria-invalid={form.formState.errors.email ? "true" : "false"}
                  aria-describedby={form.formState.errors.email ? "contact-email-error" : undefined}
                />
                {/* Error message - inline like pre-launch page */}
                {form.formState.errors.email && (
                  <div
                    id="contact-email-error"
                    role="alert"
                    aria-live="assertive"
                    className="mt-2 flex items-center gap-2"
                  >
                    <span className="text-state-error text-base font-bold" aria-hidden="true">
                      ⚠
                    </span>
                    <p className="text-sm text-state-error font-sans font-medium">
                      {form.formState.errors.email.message}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className={`block text-base font-medium mb-2 ${theme.colors.text.primary}`}>
                  {CONTACT_DIALOG_CONTENT.messageLabel}
                </label>
                <textarea
                  {...form.register("message")}
                  disabled={form.formState.isSubmitting}
                  className={cn(
                    "w-full px-4 py-2 text-base rounded-md font-sans min-h-[100px]",
                    "focus:outline-none focus:ring-2 focus:ring-brand-accent-mint transition-all duration-200",
                    "border-2",
                    theme.colors.text.primary,
                    inputClasses,
                    disabledStyles.default
                  )}
                  aria-label="Message"
                />
              </div>

              <button
                type="submit"
                disabled={form.formState.isSubmitting}
                className={cn(
                  "min-h-[2rem] sm:min-h-[2.5rem] px-4 sm:px-8 py-1 sm:py-2",
                  "text-base sm:text-xl font-bold rounded-full whitespace-nowrap",
                  "bg-brand-primary text-text-primary font-sans",
                  "border-2 border-brand-primary hover:border-brand-secondary",
                  "hover:bg-brand-primary/80 hover:text-text-primary",
                  "inline-flex items-center justify-center gap-2",
                  hoverStyles.scale,
                  focusStyles.ring,
                  disabledStyles.default
                )}
                aria-label="Send contact message"
              >
                {form.formState.isSubmitting && (
                  <Spinner variant="circle" size={20} className="text-text-primary" />
                )}
                {form.formState.isSubmitting
                  ? CONTACT_DIALOG_CONTENT.submitButton.submitting
                  : CONTACT_DIALOG_CONTENT.submitButton.default}
              </button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <ToastContainer
        theme="dark"
        position="bottom-right"
        toastStyle={{
          backgroundColor: "#1a1a1a",
          color: "#ffffff",
        }}
        style={
          {
            "--toastify-color-success": "#4ade80",
            "--toastify-text-color-success": "#ffffff",
          } as React.CSSProperties
        }
      />
    </>
  );
}

export default ContactDialog;
